import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const SEOHead = ({ 
  title = "Kitco DirectReserve | Tokenized Precious Metals Platform for Institutions",
  description = "DirectReserve™ - Institutional precious metals trading platform. Digital gold and silver with government vault storage, full auditability, and 1:1 physical backing.",
  keywords = "digital gold, tokenized precious metals, physically-backed gold, government vault storage, audited gold reserves, Kitco DirectReserve, institutional gold trading, blockchain gold",
  canonical = "",
  image = "https://customer-assets.emergentagent.com/job_kitcorebuild/artifacts/q2yxepff_Digital_White.png",
  type = "website"
}) => {
  const { i18n } = useTranslation();
  const siteUrl = "https://kitcodigitalmetals.com";
  
  // Get current language and path
  const currentLang = i18n.language || 'en';
  const pathWithoutLang = canonical.replace(/^\/(en|fr|es)/, '');
  const fullCanonical = canonical ? `${siteUrl}${canonical}` : siteUrl;

  // Language names for meta
  const languageNames = {
    'en': 'English',
    'fr': 'Français',
    'es': 'Español'
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={fullCanonical} />

      {/* Hreflang Tags for Multilingual SEO */}
      <link rel="alternate" hrefLang="en" href={`${siteUrl}${pathWithoutLang}`} />
      <link rel="alternate" hrefLang="fr" href={`${siteUrl}/fr${pathWithoutLang}`} />
      <link rel="alternate" hrefLang="es" href={`${siteUrl}/es${pathWithoutLang}`} />
      <link rel="alternate" hrefLang="x-default" href={`${siteUrl}${pathWithoutLang}`} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Kitco Digital Metals" />
      <meta property="og:locale" content={currentLang === 'fr' ? 'fr_FR' : currentLang === 'es' ? 'es_ES' : 'en_US'} />
      {currentLang === 'en' && <meta property="og:locale:alternate" content="fr_FR" />}
      {currentLang === 'en' && <meta property="og:locale:alternate" content="es_ES" />}

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={fullCanonical} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />

      {/* Additional SEO */}
      <meta name="robots" content="index, follow" />
      <meta name="language" content={languageNames[currentLang]} />
      <meta name="revisit-after" content="7 days" />
      <meta name="author" content="Kitco Digital Metals" />
      <meta httpEquiv="content-language" content={currentLang} />

      {/* Structured Data - Organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Kitco Digital Metals",
          "url": siteUrl,
          "logo": `${siteUrl}/logo.png`,
          "description": description,
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+1-833-972-2424",
            "contactType": "Customer Service",
            "areaServed": ["US", "CA", "FR", "ES", "Worldwide"],
            "availableLanguage": ["English", "Français", "Español"]
          },
          "sameAs": []
        })}
      </script>

      {/* Structured Data - Financial Service */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FinancialService",
          "name": "Kitco DirectReserve",
          "description": "Institutional turnkey precious metals trading platform with tokenized gold and silver",
          "url": `${siteUrl}/direct-reserve`,
          "provider": {
            "@type": "Organization",
            "name": "Kitco Digital Metals"
          }
        })}
      </script>
    </Helmet>
  );
};

export default SEOHead;
